import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import moment, { Moment } from 'moment'

import { useCurrentAccessWeekdays } from 'modules/core/hooks/useCurrentAccessWeekdays'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ScheduleRecordValuesType } from 'modules/project_management/components/Schedule/Body/ScheduleItem/types'
import { cellText } from 'modules/project_management/components/Schedule/Body/ScheduleItem/utils'

interface ISubRowCellHoverProps {
  record: ScheduleRecordValuesType
}

export const SubRowCellHoverHeader = ({ record }: ISubRowCellHoverProps) => {
  return (
    <Box flexDirection="column" gap="s4">
      <Box flexDirection="column" gap="s4">
        <Text size="200" weight="bold">
          {record.group}
        </Text>
        {record.title && <Text size="200">{record.title}</Text>}
        {record.subtitle && <Text size="200">{record.subtitle}</Text>}
      </Box>
      {!record.canEditAssignments && (
        <OneAlert
          description={i18n.t(
            'project_management.planning.planned_cell_hover.cant_edit_assignments'
          )}
          title={i18n.t(
            'project_management.planning.planned_cell_hover.cant_edit_assignments_title'
          )}
          variant="info"
        />
      )}
    </Box>
  )
}

export const SubRowCellHoverContent = ({ record }: ISubRowCellHoverProps) => {
  const startDate = moment(record.startDate)
  const endDate = moment(record.endDate)
  const nDays = endDate.diff(startDate, 'days') + 1

  return (
    <Box gap="s4">
      <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom={'s4'}>
        <Text size="100" weight="bold">
          {i18n.t('project_management.planning.planned_cell_hover.daily_assignation')}
        </Text>
        <Text size="100" weight="bold">
          {cellText(record.dailyMinutes, undefined, 'none')}
        </Text>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text size="100">{i18n.t('project_management.planning.planned_cell_hover.total')}</Text>
        <Text size="100">{cellText(record.dailyMinutes * nDays, undefined, 'none')}</Text>
      </Box>
    </Box>
  )
}

const formatDate = (startDate: Moment, endDate: Moment) => {
  if (startDate.isSame(endDate, 'day')) {
    return i18nDateToString(startDate, 'ui')
  }
  return `${i18nDateToString(startDate, 'ui')} - ${i18nDateToString(endDate, 'ui')}`
}

export const SubRowCellHoverFooterDates = ({ record }: ISubRowCellHoverProps) => {
  const startDate = moment(record.realStartDate)
  const endDate = moment(record.realEndDate)

  return (
    <Box flexDirection="row" justifyContent="spaceBetween">
      <Text size="100" weight="bold">
        {i18n.t('project_management.planning.header_cells_hover.date')}
      </Text>
      <Text size="100" weight="bold">
        {formatDate(startDate, endDate)}
      </Text>
    </Box>
  )
}

const getWorkDaysLabel = (type: string) => {
  switch (type) {
    case 'friday':
      return i18n.t('project_management.selectors.week_days.days.friday')
    case 'monday':
      return i18n.t('project_management.selectors.week_days.days.monday')
    case 'saturday':
      return i18n.t('project_management.selectors.week_days.days.saturday')
    case 'sunday':
      return i18n.t('project_management.selectors.week_days.days.sunday')
    case 'thursday':
      return i18n.t('project_management.selectors.week_days.days.thursday')
    case 'tuesday':
      return i18n.t('project_management.selectors.week_days.days.tuesday')
    case 'wednesday':
      return i18n.t('project_management.selectors.week_days.days.wednesday')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const WORK_DAYS_INDEX: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
}
const getWorkDaysIndex = (type: string): number => {
  const index = WORK_DAYS_INDEX[type]
  if (index === undefined) {
    throw new Error(`Unknown work day type: ${type}`)
  }
  return index
}

const formatWeekDays = (accessWeekDays: string[], weekDays: number[]): string => {
  return accessWeekDays
    .filter((day) => weekDays.includes(getWorkDaysIndex(day)))
    .map((day) => getWorkDaysLabel(day))
    .join(', ')
}

export const SubRowCellHoverFooterWeekDays = ({ record }: ISubRowCellHoverProps) => {
  const accessWeekDays = useCurrentAccessWeekdays()

  return (
    <Box flexDirection="row" justifyContent="spaceBetween">
      <Text size="100" weight="bold">
        {i18n.t('project_management.planning.header_cells_hover.days')}
      </Text>
      <Text size="100" weight="bold">
        {formatWeekDays(accessWeekDays, record?.weekDays || [])}
      </Text>
    </Box>
  )
}
